import { useFormikContext } from "formik";
import _ from "lodash";
import React from "react";
import { useSearchApplicantDuplicates } from "src/generated/hooks/form-service/queries";
import type { ApplicantDuplicate } from "src/generated/hooks/form-service/requests";
import { StudentProfileForm } from "../schemas";

const SEARCH_DEBOUNCE_MS = 350;
const RESULT_LIMIT = 10;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Drives the possible-duplicates search from the live student form values. Fires
 * only once there is at least one character of first and last name and a full
 * date of birth. The server matches by name; this hook owns the (naive) split
 * between strong matches (same date of birth) and similar names (different date
 * of birth), which is purely informational for the user.
 */
export function useStudentDuplicateSearch(organizationId: string) {
  const { values } = useFormikContext<StudentProfileForm>();
  const firstName = (values.first_name ?? "").trim();
  const lastName = (values.last_name ?? "").trim();
  const birthDate = (values.birth_date ?? "").trim();

  const isQualified =
    firstName.length >= 1 && lastName.length >= 1 && ISO_DATE.test(birthDate);
  const searchValue = `${firstName} ${lastName}`.trim();

  const [debouncedSearch, setDebouncedSearch] = React.useState(searchValue);
  const debouncedSet = React.useMemo(
    () =>
      _.debounce(
        (value: string) => setDebouncedSearch(value),
        SEARCH_DEBOUNCE_MS
      ),
    []
  );
  React.useEffect(() => {
    debouncedSet(searchValue);
    return () => debouncedSet.cancel();
  }, [searchValue, debouncedSet]);

  const { data, isLoading, isError } = useSearchApplicantDuplicates(
    {
      query: {
        organizationId,
        search: debouncedSearch,
        limit: RESULT_LIMIT,
        offset: 0,
      },
    },
    undefined,
    {
      enabled: isQualified && !!organizationId && debouncedSearch.length >= 3,
    }
  );

  const { matches, similar } = React.useMemo(() => {
    const strong: ApplicantDuplicate[] = [];
    const rest: ApplicantDuplicate[] = [];
    for (const applicant of data?.data.applicants ?? []) {
      if (applicant.birth_date === birthDate) {
        strong.push(applicant);
      } else {
        rest.push(applicant);
      }
    }
    return { matches: strong, similar: rest };
  }, [data, birthDate]);

  const hasSearched = isQualified && !isLoading && data !== undefined;

  return {
    isQualified,
    isLoading,
    isError,
    hasSearched,
    matches,
    similar,
    fullName: `${firstName} ${lastName}`.trim(),
    birthDate,
  };
}
